import React, {
	useEffect,
	useState
} from "react";
import { DataContext } from "../../../App";
import {
	Box,
	Button,
	Checkbox,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	FormGroup,
	Grid,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from "@mui/material";
import { AlertBox } from "../../Shared/Alert/Alert";
import { ViewButtonActions } from "../../Shared/ViewButton/ViewButton";
import {
	ACCESS_ALL_SELLERS_PERMISSION,
	grantablePermissions,
	groupPermissionsByCategory,
	MANAGE_ROLES_PERMISSION,
	partitionRolePermissions,
	permissionCategory,
	permissionDescription,
} from "../../../utils/permissionCatalogue";

function isGlobalCustomRole(role) {
	return Boolean(role) && !role.is_system && (role.seller_id == null || role.seller_id === undefined);
}

function roleTypeLabel(role) {
	if (role.is_system) {
		return "System";
	}
	return isGlobalCustomRole(role) ? "Global" : "Seller";
}

function permissionMatchesFilter(code, filter) {
	if (!filter) {
		return true;
	}
	const description = permissionDescription(code).toLowerCase();
	const category = permissionCategory(code).toLowerCase();
	return code.toLowerCase().includes(filter)
		|| description.includes(filter)
		|| category.includes(filter);
}

function PermissionCodeLabel({ code }) {
	return (
		<Box>
			<Typography variant="body2">
				{permissionDescription(code)}
			</Typography>
			<Typography variant="caption" color="text.secondary" component="div">
				<code>{code}</code>
			</Typography>
		</Box>
	);
}

function GroupedRolePermissions({ permissions }) {
	if (!permissions || permissions.length === 0) {
		return "—";
	}
	const groups = groupPermissionsByCategory(permissions);
	return (
		<Box>
			{groups.map(({ category, permissions: codes }) => (
				<Box key={category} sx={{ mb: 0.75, "&:last-child": { mb: 0 } }}>
					<Typography variant="caption" color="text.secondary" component="div">
						{category}
					</Typography>
					<Typography variant="body2">
						{codes.join(", ")}
					</Typography>
				</Box>
			))}
		</Box>
	);
}

// Light appbar navy. Dark mode's appbar.main is near-black and would vanish.
const CATEGORY_BAND_BG = "rgba(49,74,131,1)";

const categoryRowBgSx = {
	bgcolor: CATEGORY_BAND_BG,
	"&:nth-of-type(even)": { bgcolor: CATEGORY_BAND_BG },
	"&:hover": { bgcolor: CATEGORY_BAND_BG },
};

const categoryRowCellSx = {
	bgcolor: CATEGORY_BAND_BG,
	color: "#fff",
	fontWeight: 600,
	py: 0.75,
	borderBottom: 0,
};

const categoryHeadingSx = {
	mb: 0.5,
	px: 1,
	py: 0.5,
	bgcolor: CATEGORY_BAND_BG,
	color: "#fff",
	borderRadius: 0.5,
};

export default function RoleManagement({ seller, userdata }) {
	const context = React.useContext(DataContext);
	const canManageRoles = Boolean(userdata?.permissions?.has(MANAGE_ROLES_PERMISSION));
	const isElevated = Boolean(userdata?.permissions?.has(ACCESS_ALL_SELLERS_PERMISSION));
	const availablePermissions = grantablePermissions(userdata);

	const [state, setState] = useState({
		roles: [],
		loadError: "",
		reload: 0,
		dialogOpen: false,
		dialogMode: "create",
		dialogError: "",
		editingRole: null,
		name: "",
		selectedPermissions: [],
		// On the role but outside what the editor may grant (shown read-only; not sent on save).
		lockedPermissions: [],
		isGlobal: false,
		showPermissionGuide: false,
		permissionFilter: "",
		permissionGuideFilter: "",
	});

	useEffect(() => {
		if (!seller || !canManageRoles) {
			return;
		}
		context.dataProvider.getRoles(seller)
			.then((response) => {
				setState((prev) => ({
					...prev,
					roles: Array.isArray(response?.data) ? response.data : [],
					loadError: "",
				}));
			})
			.catch((error) => {
				const apiMessage = error?.response?.data?.message;
				const status = error?.response?.status;
				let loadError = "Unable to load roles.";
				if (status === 403) {
					loadError = typeof apiMessage === "string" && apiMessage.trim() !== ""
						? apiMessage
						: "You do not have permission to manage roles.";
				} else if (typeof apiMessage === "string" && apiMessage.trim() !== "") {
					loadError = apiMessage;
				}
				setState((prev) => ({
					...prev,
					roles: [],
					loadError,
				}));
			});
	}, [context.dataProvider, seller, canManageRoles, state.reload]);

	if (!canManageRoles) {
		return null;
	}

	const closeDialog = () => {
		setState((prev) => ({
			...prev,
			dialogOpen: false,
			dialogError: "",
			editingRole: null,
			name: "",
			selectedPermissions: [],
			lockedPermissions: [],
			isGlobal: false,
			permissionFilter: "",
		}));
	};

	const openPermissionGuide = () => {
		setState((prev) => ({
			...prev,
			showPermissionGuide: true,
		}));
	};

	const closePermissionGuide = () => {
		setState((prev) => ({
			...prev,
			showPermissionGuide: false,
			permissionGuideFilter: "",
		}));
	};

	const openCreate = () => {
		setState((prev) => ({
			...prev,
			dialogOpen: true,
			dialogMode: "create",
			dialogError: "",
			editingRole: null,
			name: "",
			selectedPermissions: [],
			lockedPermissions: [],
			isGlobal: false,
			permissionFilter: "",
		}));
	};

	const openEdit = (role) => {
		const { editable, locked } = partitionRolePermissions(
			role.permissions,
			availablePermissions,
		);
		setState((prev) => ({
			...prev,
			dialogOpen: true,
			dialogMode: "edit",
			dialogError: "",
			editingRole: role,
			name: role.name,
			selectedPermissions: editable,
			lockedPermissions: locked,
			isGlobal: isGlobalCustomRole(role),
			permissionFilter: "",
		}));
	};

	const togglePermission = (code) => {
		setState((prev) => {
			const selected = new Set(prev.selectedPermissions);
			if (selected.has(code)) {
				selected.delete(code);
			} else {
				selected.add(code);
			}
			return {
				...prev,
				selectedPermissions: Array.from(selected).sort(),
			};
		});
	};

	const selectAllPermissions = () => {
		setState((prev) => ({
			...prev,
			selectedPermissions: [...availablePermissions].sort(),
		}));
	};

	const clearPermissions = () => {
		setState((prev) => ({
			...prev,
			selectedPermissions: [],
		}));
	};

	const onSave = async () => {
		const name = (state.name || "").trim();
		if (!name) {
			setState((prev) => ({ ...prev, dialogError: "Name is required" }));
			return;
		}

		try {
			const opts = { global: Boolean(state.isGlobal) };
			if (state.dialogMode === "create") {
				await context.dataProvider.createRole(seller, name, state.selectedPermissions, opts);
			} else if (state.editingRole) {
				await context.dataProvider.updateRole(
					seller,
					state.editingRole.id,
					name,
					state.selectedPermissions,
					opts,
				);
			}
			setState((prev) => ({
				...prev,
				dialogOpen: false,
				dialogError: "",
				editingRole: null,
				name: "",
				selectedPermissions: [],
				lockedPermissions: [],
				isGlobal: false,
				reload: prev.reload + 1,
			}));
		} catch (error) {
			const apiMessage = error?.response?.data?.message;
			setState((prev) => ({
				...prev,
				dialogError: typeof apiMessage === "string" && apiMessage.trim() !== ""
					? apiMessage
					: "Unable to save role.",
			}));
		}
	};

	const onDelete = async (role) => {
		if (!window.confirm(`Delete role "${role.name}"? This only works when the role is not assigned.`)) {
			return;
		}
		try {
			await context.dataProvider.deleteRole(seller, role.id, {
				global: isGlobalCustomRole(role),
			});
			setState((prev) => ({ ...prev, reload: prev.reload + 1 }));
		} catch (error) {
			const apiMessage = error?.response?.data?.message;
			setState((prev) => ({
				...prev,
				loadError: typeof apiMessage === "string" && apiMessage.trim() !== ""
					? apiMessage
					: "Unable to delete role.",
			}));
		}
	};

	const rows = [...(state.roles || [])].sort((a, b) => {
		if (a.is_system !== b.is_system) {
			return a.is_system ? -1 : 1;
		}
		return a.name.localeCompare(b.name);
	});

	const permissionFilter = (state.permissionFilter || "").trim().toLowerCase();
	const visiblePermissions = availablePermissions.filter((code) => (
		permissionMatchesFilter(code, permissionFilter)
	));
	const visiblePermissionGroups = groupPermissionsByCategory(visiblePermissions);

	const permissionGuideFilter = (state.permissionGuideFilter || "").trim().toLowerCase();
	const guidePermissions = availablePermissions.filter((code) => (
		permissionMatchesFilter(code, permissionGuideFilter)
	));
	const guidePermissionGroups = groupPermissionsByCategory(guidePermissions);
	const lockedPermissionGroups = groupPermissionsByCategory(state.lockedPermissions);

	return (
		<Grid container sx={{ mb: 1 }}>
			<Grid item xs={12}>
				<Paper sx={{ p: 2, width: "100%" }} elevation={2}>
					<Box sx={{ display: "flex", alignItems: "center", mb: 2, gap: 2, flexWrap: "wrap" }}>
						<Typography component="h1" variant="h5">
							Roles
						</Typography>
						<Button variant="outlined" color="secondary" onClick={openCreate}>
							Add Role
						</Button>
						<Button
							variant="text"
							color="secondary"
							onClick={openPermissionGuide}
						>
							What do permissions mean?
						</Button>
					</Box>
					{state.loadError && (
						<AlertBox severity="error" message={state.loadError} sx={{ mb: 2 }} />
					)}
					<TableContainer>
						<Table size="small" stickyHeader aria-label="roles table">
							<TableHead>
								<TableRow>
									<TableCell>Name</TableCell>
									<TableCell>Type</TableCell>
									<TableCell>Permissions</TableCell>
									<TableCell />
								</TableRow>
							</TableHead>
							<TableBody>
								{rows.map((role) => (
									<TableRow key={role.id}>
										<TableCell>{role.name}</TableCell>
										<TableCell>{roleTypeLabel(role)}</TableCell>
										<TableCell>
											<GroupedRolePermissions permissions={role.permissions} />
										</TableCell>
										<TableCell align="right">
											{!role.is_system && (
												<ViewButtonActions
													actions={[
														{
															title: "Edit Role",
															onClick: () => openEdit(role),
														},
														{
															title: "Delete Role",
															onClick: () => onDelete(role),
															color: "error",
														},
													]}
												/>
											)}
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
				</Paper>
			</Grid>

			<Dialog
				open={state.dialogOpen}
				onClose={closeDialog}
				fullWidth
				maxWidth={false}
				PaperProps={{
					sx: {
						width: "70%",
						maxWidth: "70%",
					},
				}}
			>
				<DialogTitle>
					{state.dialogMode === "create" ? "Add Role" : `Edit ${state.editingRole?.name || "Role"}`}
				</DialogTitle>
				<DialogContent>
					{state.dialogError && (
						<AlertBox severity="error" message={state.dialogError} sx={{ mb: 2 }} />
					)}
					<TextField
						label="Name"
						fullWidth
						margin="normal"
						size="small"
						value={state.name}
						onChange={(e) => setState((prev) => ({ ...prev, name: e.target.value }))}
					/>
					{state.dialogMode === "create" && isElevated && (
						<FormControlLabel
							sx={{ mt: 1, display: "block" }}
							control={
								<Checkbox
									checked={state.isGlobal}
									onChange={(e) => setState((prev) => ({
										...prev,
										isGlobal: e.target.checked,
									}))}
								/>
							}
							label="Global (all sellers)"
						/>
					)}
					{state.dialogMode === "edit" && state.isGlobal && (
						<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
							Global role (all sellers)
						</Typography>
					)}
					<Box
						sx={{
							mt: 2,
							mb: 1,
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
							gap: 1,
						}}
					>
						<Typography variant="subtitle2">
							Permissions
						</Typography>
						{availablePermissions.length > 0 && (
							<Box sx={{ display: "flex", gap: 0.5 }}>
								<Button
									size="small"
									variant="text"
									color="secondary"
									onClick={selectAllPermissions}
								>
									Select all
								</Button>
								<Button
									size="small"
									variant="text"
									color="secondary"
									onClick={clearPermissions}
								>
									Clear
								</Button>
							</Box>
						)}
					</Box>
					{availablePermissions.length > 0 && (
						<TextField
							size="small"
							fullWidth
							label="Filter permissions"
							placeholder="e.g. view, order, audit"
							value={state.permissionFilter}
							onChange={(e) => setState((prev) => ({
								...prev,
								permissionFilter: e.target.value,
							}))}
							sx={{ mb: 1 }}
						/>
					)}
					<FormGroup sx={{ maxHeight: "50vh", overflow: "auto" }}>
						{visiblePermissions.length === 0 ? (
							<Typography variant="body2" color="text.secondary">
								No permissions match this filter.
							</Typography>
						) : (
							visiblePermissionGroups.map(({ category, permissions: codes }) => (
								<Box key={category} sx={{ mb: 1.5, "&:last-child": { mb: 0 } }}>
									<Typography variant="subtitle2" component="h3" sx={categoryHeadingSx}>
										{category}
									</Typography>
									{codes.map((code) => (
										<FormControlLabel
											key={code}
											control={
												<Checkbox
													checked={state.selectedPermissions.includes(code)}
													onChange={() => togglePermission(code)}
												/>
											}
											label={<PermissionCodeLabel code={code} />}
										/>
									))}
								</Box>
							))
						)}
					</FormGroup>
					{state.lockedPermissions.length > 0 && (
						<Box sx={{ mt: 2 }}>
							<Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
								On this role but outside what you may grant. Saving removes them
								(the API rejects permissions you do not hold).
							</Typography>
							<FormGroup>
								{lockedPermissionGroups.map(({ category, permissions: codes }) => (
									<Box key={category} sx={{ mb: 1.5, "&:last-child": { mb: 0 } }}>
										<Typography variant="subtitle2" component="h3" sx={categoryHeadingSx}>
											{category}
										</Typography>
										{codes.map((code) => (
											<FormControlLabel
												key={code}
												disabled
												control={<Checkbox checked />}
												label={<PermissionCodeLabel code={code} />}
											/>
										))}
									</Box>
								))}
							</FormGroup>
						</Box>
					)}
				</DialogContent>
				<DialogActions sx={{ justifyContent: "center", pb: 2 }}>
					<Button variant="outlined" onClick={closeDialog}>Cancel</Button>
					<Button variant="contained" onClick={onSave} sx={{ ml: 2 }}>Save</Button>
				</DialogActions>
			</Dialog>

			<Dialog
				open={state.showPermissionGuide}
				onClose={closePermissionGuide}
				fullWidth
				maxWidth={false}
				PaperProps={{
					sx: {
						width: "75%",
						maxWidth: "75%",
					},
				}}
				aria-labelledby="permission-guide-title"
			>
				<DialogTitle id="permission-guide-title">
					What do permissions mean?
				</DialogTitle>
				<DialogContent>
					<Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
						A role is a named set of these permissions. You can only put
						permissions on a role if you hold them yourself. Holding an edit
						or create permission for an area also unlocks the matching view
						access for that area.
					</Typography>
					<TextField
						size="small"
						fullWidth
						label="Filter guide"
						placeholder="e.g. view, order, claim"
						value={state.permissionGuideFilter}
						onChange={(e) => setState((prev) => ({
							...prev,
							permissionGuideFilter: e.target.value,
						}))}
						sx={{ mb: 1.5 }}
					/>
					<TableContainer sx={{ maxHeight: "50vh" }}>
						<Table size="small" stickyHeader aria-label="permission meanings">
							<TableHead>
								<TableRow>
									<TableCell>Meaning</TableCell>
									<TableCell>Permission</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{guidePermissionGroups.map(({ category, permissions: codes }) => (
									<React.Fragment key={category}>
										<TableRow sx={categoryRowBgSx}>
											<TableCell colSpan={2} sx={categoryRowCellSx}>
												{category}
											</TableCell>
										</TableRow>
										{codes.map((code) => (
											<TableRow key={code}>
												<TableCell>{permissionDescription(code)}</TableCell>
												<TableCell
													sx={{
														whiteSpace: "nowrap",
														color: "text.secondary",
													}}
												>
													<code>{code}</code>
												</TableCell>
											</TableRow>
										))}
									</React.Fragment>
								))}
							</TableBody>
						</Table>
					</TableContainer>
					{guidePermissions.length === 0 && (
						<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
							No permissions match this filter.
						</Typography>
					)}
				</DialogContent>
				<DialogActions sx={{ justifyContent: "center", pb: 2 }}>
					<Button variant="outlined" onClick={closePermissionGuide}>Close</Button>
				</DialogActions>
			</Dialog>
		</Grid>
	);
}
